import { Component, OnInit, inject, DestroyRef, effect } from '@angular/core';
import { Validators, FormBuilder, ValidatorFn, AbstractControl, ValidationErrors, FormGroup, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IEntityGroup } from '@app/core/services/custom-services/entity-group.service';
import { EntityPropertyService } from '@app/core/services/http-services/model/entity-property.service';
import { EntityRelationDirection, EntityService, IEntity, IEntityRelation, IPropertyValue } from '@app/core/services/http-services/model/entity.service';
import { UnitService } from '@app/core/services/http-services/common/unit-service';
import { AppNotificationService } from '@app/core/services/custom-services/notification.service';
import { _, TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RegexHelper } from '@app/shared/helpers/regex-helper';
import { ProdOrderService } from '@app/core/services/http-services/operative/prod-order.service';
import { IUnit } from '@app/core/models/unit.model';
import { IUnitConversion } from '@app/core/models/unitConversion.model';
import { EntityPropertyDataTypeEnum } from '@app/core/models/entity-property-data-type.enum';
import { DialogRef, DialogContentBase, KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { BeamLamminaDimension } from '@app/core/models/beam-lammina-dimension.model';
import { DimensionService } from '@app/core/services/http-services/gluelam/dimension.service';
import { SVGIcon, minusIcon, plusIcon } from '@progress/kendo-svg-icons';
import { Guid } from '@app/core/models/Guid';
import { KENDO_FORM, KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { SelectEntityStatusComponent } from '@app-module/app-controls/select-entity-status/select-entity-status.component';
import { SelectValidValueComponent } from '@app-module/app-controls/select-valid-value/select-valid-value.component';
import { SelectEntityComponent } from '@app-module/app-controls/select-entity/select-entity.component';
import { SelectEntityRelationDirectionComponent } from '@app-module/app-controls/select-entity-relation-direction/select-entity-relation-direction.component';
import { ConfirmationDialogComponent } from '@app/core/components/confirmation-dialog/confirmation-dialog.component';
import { EntityStatusEnum } from '@app/core/models/entity-status.enum';
import { CreateEntityForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-form.model';
import { CreatePropertyValueEntityForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-property-value-form.model';
import { CreateEntityRelationForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-relation-form.model';
import { CreateEntityUnitConversionForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-unit-conversion-form.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { IEntityProperty } from '@app/core/models/entity-property.model';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_LABELS } from '@progress/kendo-angular-label';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';

_([
  'EntityTranslation.MinStockShouldbeGreaterThanZero',
  'EntityTranslation.MaxStockShouldbeGreaterThanZero',
  'EntityTranslation.MaxStockShouldbeGreater',
  'EntityTranslation.MinStockNotEqualToMax',
  'EntityTranslation.EntityRelationInvalidRelationForConsume',
  'EntityTranslation.InstructionFileDeleted',
  'EntityTranslation.EntityCodeLengthError',
  'EntityTranslation.InvalidDataType',
  'EntityTranslation.Activity',
  'EntityTranslation.Material',
  'EntityTranslation.ConfirmBudgetRemovalMsg',
  'EntityTranslation.ConfirmEntityRelationRemoval',
  'Validation.RelationCannotBeZero',
  'Validation.ProducesRequiresPositive',
  'Validation.HourRequiresNegative',
  'Validation.ConsumesRequiresNegative'
]);

@Component({
  selector: 'app-entity-form',
  templateUrl: './entity-form.component.html',
  styleUrls: ['./entity-form.component.css'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateDirective,
    SelectEntityStatusComponent,
    SelectValidValueComponent,
    SelectEntityComponent,
    SelectEntityRelationDirectionComponent,
    TranslatePipe,
    ConfirmationDialogComponent,
    KENDO_GRID,
    KENDO_INPUTS,
    KENDO_FORM,
    KENDO_DIALOG,
    KENDO_DROPDOWNS,
    KENDO_LABELS,
    KENDO_BUTTONS,
  ],
})
export class EntityFormComponent extends DialogContentBase implements OnInit {

  _entity: IEntity;
  _entityGroup: IEntityGroup;
  _entityRelation: IEntityRelation;
  units: IUnit[] = [];
  disableSubmit: boolean = false;
  entityForm!: FormGroup<CreateEntityForm>;
  isNew: boolean;
  confirmBudgetRemove: boolean = false;
  confirmRemoveRelationIndex: number | null = null;

  minusIcon: SVGIcon = minusIcon;
  plusIcon: SVGIcon = plusIcon;

  translationService = inject(TranslateService);

  dimensions: BeamLamminaDimension[] = [];
  noDimensionItem = { beamLamminaDimensionID: null, description: this.translationService.instant('App.SelectItem') };

  get entityRelations(): FormArray<FormGroup<CreateEntityRelationForm>> {
    return this.entityForm.controls.entityRelations;
  }

  get entityRelationGridData(): { relationGroup: FormGroup<CreateEntityRelationForm>; formIndex: number }[] {
    return this.entityRelations.controls.map((relationGroup, formIndex) => ({ relationGroup, formIndex })).filter(item => item.relationGroup.value?.isRemoved === false);
  }

  newEntityRelation(): FormGroup<CreateEntityRelationForm> {
    return this.createEntityRelationFormGroup(
      <IEntityRelation>{
        sortOrder: this.getNextRelationSortOrder(),
        relation: null,
        direction: EntityRelationDirection.CONSUMES,
        unitCode: 'M3',
        userCode: null,
        inTactical: true,
        inOperative: true,
        isChaining: false,
        batchTemplateId: null,
        isPreferred: false,
      },
      0,
      true
    );
  }

  newUnitConversion(): FormGroup<CreateEntityUnitConversionForm> {
    return this.createUnitConversionFormGroup(
      <IUnitConversion>{
        unitCode: null,
        convFactor: null,
        entityIndex: this.data.entity.index,
        index: 0,
        id: 0,
      },
      true
    );
  }

  addNewunitConversion() {
    this.unitConversions.push(this.newUnitConversion());
  }

  addNewEntityRelation() {
    this.entityRelations.push(this.newEntityRelation());
    this.recalculateEntityRelationSortOrder();
  }

  onEntityRelationEntitySelected(entity: IEntity, index: number) {
    const row = this.entityRelations.at(index);
    if (entity?.baseUnitCode && (row.value.isNew || !row.value.unitCode)) {
      row.controls.unitCode.patchValue(entity.baseUnitCode);
    }
  }

  removeEntityRelation(entityIndex: number) {
    this.confirmRemoveRelationIndex = entityIndex;
  }

  confirmEntityRelationRemoval(isConfirmed: boolean) {
    if (isConfirmed && this.confirmRemoveRelationIndex !== null) {
      const index = this.confirmRemoveRelationIndex;
      this.entityRelations.at(index).controls.isRemoved.patchValue(true);
      if (this.entityRelations.at(index).value.isNew) {
        this.entityRelations.removeAt(index);
      }
      this.recalculateEntityRelationSortOrder();
    }
    this.confirmRemoveRelationIndex = null;
  }

  removeUnitConversion(Index: number) {
    if (this.unitConversions.at(Index).value.isNew) {
      this.unitConversions.removeAt(Index);
    } else {
      this.unitConversions.at(Index).controls.isRemoved.patchValue(true);
    }
  }

  buildForm() {
    this.entityForm = this.formBuilder.group<CreateEntityForm>({
      index: this.formBuilder.control(this._entity.index, [Validators.required]),
      code: this.formBuilder.control(this._entity.code, [Validators.required, Validators.maxLength(100)]),
      baseUnitCode: this.formBuilder.control(this._entity.baseUnitCode ?? '', [Validators.required]),
      changeDate: this.formBuilder.control(this._entity.changeDate),
      commentId: this.formBuilder.control(this._entity.commentId),
      description: this.formBuilder.control(this._entity.description, [Validators.required]),
      entityGroupCode: this.formBuilder.control(this._entity.entityGroupCode),
      isDynamicActivity: this.formBuilder.control(this._entity.isDynamicActivity),
      sortOrderIndex: this.formBuilder.control(this._entity.sortOrderIndex),
      status: this.formBuilder.control(this._entity.status, [Validators.required]),
      superiorEntityIndex: this.formBuilder.control(this._entity.superiorEntityIndex),
      userCode: this.formBuilder.control(this._entity.userCode),
      machineId: this.formBuilder.control(this._entity.machineId),
      instruction: this.formBuilder.control(this._entity.instruction),
      propertyValues: this.formBuilder.array(this._entity.propertyValues.map(pv => this.createPropertyValueFormGroup(pv))),
      entityRelations: this.formBuilder.array(
        this._entity.entityRelations.map(enr => this.createEntityRelationFormGroup(enr, enr.activityIndex, false)),
        { validators: this.validateEntityRelationArray() }
      ),
      unitConversions: this.formBuilder.array(
        this._entity.unitConversions?.map(uc => this.createUnitConversionFormGroup(uc, false)),
        { validators: this.validateUnitConversionArray() }
      ),
      targetMinStock: this.formBuilder.control(this._entity.targetMinStock, Validators.pattern(RegexHelper.onlyPostiveWithDotComma)),
      targetMaxStock: this.formBuilder.control(this._entity.targetMaxStock, Validators.pattern(RegexHelper.onlyPostiveWithDotComma)),
      batchTime: this.formBuilder.control(this._entity.batchTime),
      batchSize: this.formBuilder.control(this._entity.batchSize, Validators.pattern(RegexHelper.onlyPostiveWithDotComma)),
      guid: this.formBuilder.control(this._entity.guid),
      beamLamminaDimensionId: this.formBuilder.control(this._entity.beamLamminaDimensionId),
      autoGenerateProdOrder: this.formBuilder.control(this._entity.autoGenerateProdOrder),
    });

    this.recalculateEntityRelationSortOrder();
  }

  createPropertyValueFormGroup(pv: IPropertyValue): FormGroup<CreatePropertyValueEntityForm> {
    return this.formBuilder.group<CreatePropertyValueEntityForm>({
      id: this.formBuilder.control(pv.id),
      entityIndex: this.formBuilder.control(pv.entityIndex),
      code: this.formBuilder.control(pv.propertyCode),
      value: this.formBuilder.control(this.getStringValue(pv)),
      dataType: this.formBuilder.control(pv.dataType),
      isMandatory: this.formBuilder.control(pv.isMandatory),
      areValuesValidated: this.formBuilder.control(pv.areValuesValidated),
      description: this.formBuilder.control(pv.description)
    });
  }

  createEntityRelationFormGroup(enr: IEntityRelation, entityIndex: number, isNew: boolean): FormGroup<CreateEntityRelationForm> {
    const formGroup = this.formBuilder.group<CreateEntityRelationForm>({
      sortOrder: this.formBuilder.control<number>(enr.sortOrder ?? 0),
      entityIndex: this.formBuilder.control<number>({ value: entityIndex, disabled: !isNew }),
      relation: this.formBuilder.control<number>(enr.relation, [Validators.required]),
      direction: this.formBuilder.control<EntityRelationDirection>(enr.direction),
      unitCode: this.formBuilder.control<string>(enr.unitCode),
      userCode: this.formBuilder.control<string>(enr.userCode),
      changeDate: this.formBuilder.control<string>(enr.ChangeDate),
      productCode: this.formBuilder.control<string>(enr.productCode),
      activityCode: this.formBuilder.control<string>(enr.activityCode),
      productIndex: this.formBuilder.control<number>(enr.productIndex),
      activityIndex: this.formBuilder.control<number>(enr.activityIndex),
      isNew: this.formBuilder.control<boolean>(isNew),
      isChange: this.formBuilder.control<boolean>(false),
      isRemoved: this.formBuilder.control<boolean>(false),
      inTactical: this.formBuilder.control<boolean>(enr.inTactical),
      inOperative: this.formBuilder.control<boolean>(enr.inOperative),
      isChaining: this.formBuilder.control<boolean>(enr.isChaining),
      batchTemplateId: this.formBuilder.control<Guid | null>(enr.batchTemplateId),
      isPreferred: this.formBuilder.control<boolean>(enr.isPreferred),
    }, { validators: this.entityRelationGroupValidator() });

    return formGroup;
  }

  createUnitConversionFormGroup(uc: IUnitConversion, isNew: boolean): FormGroup<CreateEntityUnitConversionForm> {
    return this.formBuilder.group<CreateEntityUnitConversionForm>({
      index: this.formBuilder.control(uc.index),
      entityIndex: this.formBuilder.control(uc.entityIndex),
      unitCode: this.formBuilder.control(uc.unitCode, [Validators.required]),
      convFactor: this.formBuilder.control(uc.convFactor, [Validators.required, Validators.pattern(RegexHelper.onlyPostiveWithDotCommaWithNoScaleNoZero)]),
      userCode: this.formBuilder.control(uc.userCode),
      changeDate: this.formBuilder.control(uc.changeDate),
      isNew: this.formBuilder.control(isNew),
      isChange: this.formBuilder.control(false),
      isRemoved: this.formBuilder.control(false),
      id: this.formBuilder.control(uc.id),
      mandatoryUnitRelation: this.formBuilder.control(uc.mandatoryUnitRelation),
    });
  }

  public isBaseUnit(unitCode: string): boolean {
    return unitCode === this.entityForm.controls.baseUnitCode.value;
  }

  private readonly unitService: UnitService = inject(UnitService);
  private readonly entityService: EntityService = inject(EntityService);
  private readonly poService: ProdOrderService = inject(ProdOrderService);
  private readonly entityPropertyService: EntityPropertyService = inject(EntityPropertyService);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly appNotificationService: AppNotificationService = inject(AppNotificationService);
  private readonly dimensionService: DimensionService = inject(DimensionService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public data: { entity: IEntity; entityGroup: IEntityGroup; isNew: boolean; entityTypeCode: string };

  private props: IEntityProperty[] = [];

  getProperty(code: string): IEntityProperty {
    const property = this.props.find(p => p.code === code);
    if (property) {
      return property;
    }

    const propertyValue = this._entity?.propertyValues?.find(pv => pv.propertyCode === code);
    return {
      code,
      description: propertyValue?.description ?? code,
      name: code,
      propertyGroupCode: '',
      dataType: propertyValue?.dataType ?? EntityPropertyDataTypeEnum.DATA_TYPE_STRING,
      areValuesValidated: propertyValue?.areValuesValidated ?? false,
      sortOrderIndex: 0,
      userCode: '',
      changeDate: null,
      entityGroupProperties: [],
      validValues: [],
    };
  }

  constructor(private readonly dialogRef: DialogRef) {
    super(dialogRef);
    effect(() => {
      if (this.data?.entityGroup) {
        this.entityPropertyService.getEntityPropertysForGroup(this.data.entityGroup.index).subscribe(s => {
          this.props = s;
        });
      }
    });
  }

  ngOnInit() {
    this._entity = this.data.entity;
    this._entityGroup = this.data.entityGroup;
    this.isNew = this.data.isNew;
    this.buildForm();

    this.unitService
      .query({})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(u => {
        this.units = u;
        this.refreshUnitConversionEditableState();
      });

    this.dimensionService
      .getBeamLamminaDimentions(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(d => {
        this.dimensions = d;
      });

    this.entityForm.controls.baseUnitCode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshUnitConversionEditableState());
  }

  show(c: FormGroup<CreateEntityUnitConversionForm>): boolean {
    if (this.entityForm.controls.baseUnitCode.value === c.controls.unitCode.value) {
      c.controls.convFactor.patchValue(1);
    }
    return this.entityForm.controls.baseUnitCode.value !== c.controls.unitCode.value;
  }

  onSubmit() {
    const formValue = this.entityForm.getRawValue();
    if (formValue.status === EntityStatusEnum.Archived && !this.confirmBudgetRemove && this.data.entity.isConnectedToMaterialBudget) {
      this.confirmBudgetRemove = true;
      return;
    }

    if (this.validate()) {
      const e: IEntity = <IEntity>{
        index: formValue.index ?? 0,
        code: formValue.code,
        baseUnitCode: formValue.baseUnitCode,
        changeDate: formValue.changeDate,
        commentId: formValue.commentId,
        description: formValue.description,
        entityGroup: this._entityGroup,
        entityGroupCode: formValue.entityGroupCode,
        sortOrderIndex: formValue.sortOrderIndex,
        status: formValue.status,
        userCode: formValue.userCode,
        machineId: formValue.machineId,
        targetMinStock: formValue.targetMinStock ?? null,
        targetMaxStock: formValue.targetMaxStock ?? null,
        batchTime: formValue.batchTime ?? null,
        batchSize: formValue.batchSize ?? null,
        gluSpecHightMin: null,
        gluSpecHightMax: null,
        gluSpecAddedCalcHight: null,
        gluSpecAddedCalcWidth: null,
        gluSpecBottomPercentage: null,
        gluSpecTopPercentage: null,
        guid: formValue.guid ?? Guid.newGuid(),
        beamLamminaDimensionId: formValue.beamLamminaDimensionId,
        instruction: formValue.instruction,
        propertyValues: formValue.propertyValues
          .filter(c => c.value != null && c.value !== '')
          .map(c => {
            return <IPropertyValue>{
              id: c.id,
              entityIndex: formValue.index,
              propertyCode: c.code,
              valueInt: c.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT ? +c.value : 0,
              valueDouble: c.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE ? +c.value : 0,
              valueString: c.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_STRING ? c.value : '',
              userCode: 'API',
              changeDate: new Date().toISOString(),
              dataType: c.dataType,
            };
          }),
        entityRelations: formValue.entityRelations.map(erc => {
          return <IEntityRelation>{
            sortOrder: erc.sortOrder,
            productIndex: this._entity.index,
            activityIndex: erc.entityIndex,
            relation: this.poService.validateQuantityAsPerDirection(Number(erc.relation), Number(erc.direction)),
            direction: erc.direction,
            unitCode: erc.unitCode,
            userCode: erc.userCode,
            ChangeDate: new Date().toISOString(),
            productCode: erc.productCode,
            activityCode: erc.activityCode,
            isNew: erc.isNew,
            isChange: true,
            isRemoved: erc.isRemoved,
            inTactical: erc.inTactical,
            inOperative: erc.inOperative,
            isChaining: erc.isChaining,
            batchTemplateId: erc.batchTemplateId,
            isPreferred: erc.isPreferred,
            glulamSpec: [],
          };
        }),
        unitConversions: this.unitConversions.controls.map<IUnitConversion>(c => {
          // getRawValue() is required here since convFactor/unitCode may be disabled for the base-unit row
          const rawValue = c.getRawValue();
          return <IUnitConversion>{
            index: rawValue.index ?? 0,
            entityIndex: this._entity.index ? this._entity.index : 0,
            unitCode: rawValue.unitCode,
            convFactor: rawValue.convFactor,
            userCode: 'API',
            changeDate: new Date().toISOString(),
            isNew: this.isNew,
            isChange: true,
            isRemoved: rawValue.isRemoved,
            id: rawValue.id ?? 0,
            mandatoryUnitRelation: rawValue.mandatoryUnitRelation,
          };
        }),
        autoGenerateProdOrder: formValue.autoGenerateProdOrder ?? false,
      };

      this.disableSubmit = true;
      if (e.index === -1) {
        this.entityService
          .insert(e)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.disableSubmit = false;
              this.dialogRef.close(true);
            },
            error: () => {
              this.disableSubmit = false;
            },
          });
      } else {
        this.entityService
          .update(e)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.disableSubmit = false;
              this.dialogRef.close(true);
            },
            error: () => {
              this.disableSubmit = false;
            },
          });
      }
    }
  }

  isMandatoryUnit(unitCode: string): boolean {
    return this.units?.find(u => u.unitCode === unitCode)?.mandatoryUnitRelation ?? false;
  }

  override ngAfterViewInit() {
    setTimeout(() => {
      (document.activeElement as HTMLElement | null)?.blur();
    }, 0);
  }

  get unitConversions(): FormArray<FormGroup<CreateEntityUnitConversionForm>> {
    return this.entityForm.controls.unitConversions;
  }

  get unitConversionsActive(): FormGroup<CreateEntityUnitConversionForm>[] {
    return this.unitConversions.controls.filter(c => c.value.isRemoved === false);
  }

  get propertyValues(): FormArray<FormGroup<CreatePropertyValueEntityForm>> {
    return this.entityForm.controls.propertyValues;
  }

  private validate(): boolean {
    // Entity min max stock validation
    const minStock = this.entityForm.value.targetMinStock;
    const maxStock = this.entityForm.value.targetMaxStock;

    if (minStock != null) {
      if (maxStock != null && maxStock < minStock) {
        this.showMessage('EntityTranslation.MaxStockShouldbeGreater');
        return false;
      } else if (maxStock != null && minStock > 0 && maxStock > 0 && maxStock === minStock) {
        this.showMessage('EntityTranslation.MinStockNotEqualToMax');
        return false;
      }
    }
    // Entity relation validation with direction
    const invalidEntityRelationIndex: number[] = [];
    this.entityRelations.controls.forEach((erc, index) => {
      if (erc.value.direction === EntityRelationDirection.CONSUMES && Number(erc.value.relation) === 0) {
        invalidEntityRelationIndex.push(index + 1);
      }
    });

    if (invalidEntityRelationIndex.length > 0) {
      this.appNotificationService.notifyErrorAppChanel('EntityTranslation.EntityRelationInvalidRelationForConsume', 'Error', { lineNo: invalidEntityRelationIndex.join(', ') });
      return false;
    }

    // Property value data type validation
    const propsWithoutAreValuesValidated = this._entityGroup.entityGroupProperties.filter(x => !x.property?.areValuesValidated);
    if (propsWithoutAreValuesValidated.length > 0) {
      let dataTypes = this.entityPropertyService.getDataTypeMap();

      for (const element of propsWithoutAreValuesValidated) {
        const prop = this.propertyValues.controls.find(control => control.controls.code.value === element.propertyCode);
        const dataType = prop?.controls.dataType.value;

        if (prop && this.isInvalidProperty(prop, dataType)) {
          this.appNotificationService.notifyErrorAppChanel('EntityTranslation.InvalidDataType', 'Error', {
            propertyCode: prop.value.code,
            dataType: (dataTypes ?? []).find(x => x.value == dataType)?.text,
          });
          return false;
        }
      }
    }
    return true;
  }

  private isInvalidProperty(prop: FormGroup<CreatePropertyValueEntityForm>, dataType: number | null | undefined): boolean {
    const value = prop.controls.value.value;
    if (value !== null && value !== 'null' && value?.toString().trim() !== '') {
      const valueAsNumber = Number(value);

      if ((dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE && Number.isNaN(valueAsNumber)) || (dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT && !Number.isInteger(valueAsNumber))) return true;
    }
    return false;
  }

  private showMessage(message: string) {
    this.appNotificationService.notifyErrorAppChanel(message);
  }

  private refreshUnitConversionEditableState(): void {
    if (!this.entityForm) {
      return;
    }

    const baseUnitCode = this.entityForm.controls.baseUnitCode.value;
    this.unitConversions.controls.forEach(group => {
      if (group.controls.isNew.value) return;

      // Enable/Disable emits events. We set emitEvent to false to prevent triggering valueChanges subscriptions.
      // For example, open dialogue, close dialogue, open again; now angular will get confused.
      group.controls.unitCode.enable({ emitEvent: false });
      group.controls.convFactor.enable({ emitEvent: false });

      if (group.controls.unitCode.value === baseUnitCode) {
        group.controls.convFactor.setValue(1.0, { emitEvent: false });
        group.controls.convFactor.disable({ emitEvent: false });
        group.controls.unitCode.disable({ emitEvent: false });
        return;
      }

      if (this.isMandatoryUnit(group.controls.unitCode.value)) {
        group.controls.unitCode.disable({ emitEvent: false });
        return;
      }

      if (!this.canChangeUnit(group.controls.unitCode.value)) {
        group.controls.unitCode.disable({ emitEvent: false });
      }
    });
  }

  canChangeUnit(unitCode: string): boolean {
    if (this.isMandatoryUnit(unitCode) === true) {
      return false;
    }
    if (unitCode === this.entityForm.controls.baseUnitCode.value) {
      return false;
    }

    return true;
  }

  isRelationInvalid(relationGroup: FormGroup<CreateEntityRelationForm>): boolean {
    return (
      (relationGroup.hasError('hourRequiresNegative') || relationGroup.hasError('relationCannotBeZero') || relationGroup.hasError('consumesRequiresNegative') || relationGroup.hasError('producesRequiresPositive')) &&
      (relationGroup.controls.relation.touched || relationGroup.controls.relation.dirty)
    );
  }

  isDirectionInvalid(relationGroup: FormGroup<CreateEntityRelationForm>): boolean {
    return (
      (relationGroup.hasError('consumesRequiresNegative') || relationGroup.hasError('producesRequiresPositive')) &&
      (relationGroup.controls.direction.touched || relationGroup.controls.direction.dirty)
    );
  }

  getRelationErrorKey(relationGroup: FormGroup<CreateEntityRelationForm>): string | null {
    const relationControl = relationGroup.controls.relation;
    if (!relationControl.touched && !relationControl.dirty) {
      return null;
    }

    if (relationGroup.hasError('relationCannotBeZero')) {
      return 'Validation.RelationCannotBeZero';
    }

    if (relationGroup.hasError('hourRequiresNegative')) {
      return 'Validation.HourRequiresNegative';
    }

    if (relationGroup.hasError('consumesRequiresNegative')) {
      return 'Validation.ConsumesRequiresNegative';
    }

    if (relationGroup.hasError('producesRequiresPositive')) {
      return 'Validation.ProducesRequiresPositive';
    }

    return null;
  }

  getDirectionErrorKey(relationGroup: FormGroup<CreateEntityRelationForm>): string | null {
    const directionControl = relationGroup.controls.direction;
    if (!directionControl.touched && !directionControl.dirty) {
      return null;
    }

    if (relationGroup.hasError('consumesRequiresNegative')) {
      return 'Validation.ConsumesRequiresNegative';
    }

    if (relationGroup.hasError('producesRequiresPositive')) {
      return 'Validation.ProducesRequiresPositive';
    }

    return null;
  }

  isMandatoryPropertyInvalid(propertyControl: FormGroup<CreatePropertyValueEntityForm>): boolean {
    const valueControl = propertyControl.controls.value;
    const isMandatory = propertyControl.controls.isMandatory.value === true;

    if (isMandatory && !valueControl.hasValidator(Validators.required)) {
      valueControl.addValidators(Validators.required);
      valueControl.updateValueAndValidity({ emitEvent: false });
    }

    if (!isMandatory && valueControl.hasValidator(Validators.required)) {
      valueControl.removeValidators(Validators.required);
      valueControl.updateValueAndValidity({ emitEvent: false });
      return false;
    }

    return isMandatory && valueControl.invalid && (valueControl.touched || valueControl.dirty);
  }

  confirmBudgetRemoval(isConfirmed: boolean) {
    if (isConfirmed) {
      this.onSubmit();
    }
    this.confirmBudgetRemove = false;
  }

  confirmEntityRelationRemovalMsg(): string {
    return this.translateService.instant('EntityTranslation.ConfirmEntityRelationRemoval');
  }

  confirmBudgetRemovalMsg(): string {
    let msg = '';

    this.translateService
      .get('EntityTranslation.ConfirmBudgetRemovalMsg', {
        entityDescription: this.entityForm.value.description,
      })
      .subscribe(x => {
        msg = x;
      });
    return msg;
  }

  private recalculateEntityRelationSortOrder(): void {
    let sortOrder = 1;
    this.entityRelations.controls.forEach(relationGroup => {
      if (relationGroup.value?.isRemoved) {
        return;
      }

      relationGroup.controls.sortOrder.patchValue(sortOrder++, { emitEvent: false });
    });
  }

  private getNextRelationSortOrder(): number {
    return this.entityRelations.controls.filter(c => !c.value?.isRemoved).length + 1;
  }

  // validators

  getStringValue = (pv: IPropertyValue): string => {
    if (pv.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT) {
      return pv?.valueInt?.toString() ?? '';
    } else if (pv.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE) {
      return pv?.valueDouble?.toString() ?? '';
    } else {
      return pv?.valueString ?? '';
    }
  };

  validateUnitConversionArray(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const formArray = control as FormArray<FormGroup<CreateEntityUnitConversionForm>>;
      const notRemoved = formArray.controls.filter(c => c.controls.isRemoved.value === false);
      const uniqueValues = new Set(notRemoved.map(c => c.controls.unitCode.value));

      if (uniqueValues.size < notRemoved.length) {
        return { error: 'Unit code already exist!!!' };
      }
      return null;
    };
  }

  validateEntityRelationArray(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const formArray = control as FormArray<FormGroup<CreateEntityRelationForm>>;
      let preferredConsumeCount = 0;
      let preferredProduceCount = 0;

      for (const item of formArray.controls) {
        if (!item.controls.isPreferred.value) continue;

        if (item.controls.direction.value === EntityRelationDirection.CONSUMES) {
          preferredConsumeCount++;
          if (preferredConsumeCount > 1) {
            return { error: 'Only one entity can be marked as "Preferred for Consumption".' };
          }
        } else if (item.controls.direction.value === EntityRelationDirection.PRODUCE) {
          preferredProduceCount++;
          if (preferredProduceCount > 1) {
            return { error: 'Only one entity can be marked as "Preferred for Production".' };
          }
        }
      }
      return null;
    };
  }

  private entityRelationGroupValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup<CreateEntityRelationForm>;
      const relation = group.controls.relation.value;
      const unitCode = (group.controls.unitCode.value ?? '').toString().trim().toUpperCase();
      const direction = group.controls.direction.value;

      if (relation == null || Number.isNaN(relation)) {
        return null;
      }

      if (relation === 0) {
        return { relationCannotBeZero: true };
      }

      if (unitCode === 'HOUR' && relation >= 0) {
        return { hourRequiresNegative: true };
      }

      if (direction === EntityRelationDirection.CONSUMES && relation > 0) {
        return { consumesRequiresNegative: true };
      }

      if (direction === EntityRelationDirection.PRODUCE && relation < 0) {
        return { producesRequiresPositive: true };
      }

      return null;
    };
  }
}
